import { useEffect, useState } from 'react';
import { api } from '../../lib/api-client';
import { ApiError } from '@sw/api-client';

/**
 * Live domain check del email del user contra el accessLinkToken.
 *
 * Comportamiento:
 * - Debounce 350ms post-tipeo para no spammear el backend mientras el user tipea
 * - Skip query si email es muy corto, no contiene "@", o accessLinkToken es null
 * - Cancela la request previa si el email cambia durante el debounce
 * - Estado terna: 'idle' | 'authorized' | 'unauthorized' (sin distinción explícita
 *   entre "backend respondió false por dominio" vs "backend respondió false por link
 *   inválido" — anti-enumeration; el frontend tampoco lo distingue)
 *
 * NOTA: NO mostramos el resultado si accessLinkToken está vacío — el caller debería
 * verificarlo antes de renderizar el hint, pero por defensa el hook también skip-ea.
 */
const DEBOUNCE_MS = 350;

export type DomainCheckStatus = 'idle' | 'checking' | 'authorized' | 'unauthorized';

export function useDomainCheck(
  email: string,
  accessLinkToken: string | null,
): { status: DomainCheckStatus; domain: string } {
  const [status, setStatus] = useState<DomainCheckStatus>('idle');
  const [resolvedDomain, setResolvedDomain] = useState<string>('');

  const domain = extractDomain(email);

  useEffect(() => {
    if (!accessLinkToken || !looksLikeCompleteDomain(domain)) {
      setStatus('idle');
      setResolvedDomain('');
      return;
    }

    let cancelled = false;
    setStatus('checking');

    const timer = setTimeout(async () => {
      try {
        const res = await api.post<{ authorized: boolean }>('/api/client/auth/check-domain', {
          email,
          accessLinkToken,
        });
        if (cancelled) return;
        setResolvedDomain(domain);
        setStatus(res.authorized ? 'authorized' : 'unauthorized');
      } catch (err) {
        if (cancelled) return;
        // Errores del backend (429, 5xx) — preferimos no mostrar hint en vez de uno engañoso.
        setStatus('idle');
        setResolvedDomain('');
        // El reporter global ya captura el ApiError vía MutationCache.onError.
        void err;
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [email, domain, accessLinkToken]);

  return { status, domain: resolvedDomain };
}

function extractDomain(email: string): string {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at < 0 || at === trimmed.length - 1) return '';
  return trimmed.slice(at + 1);
}

function looksLikeCompleteDomain(domain: string): boolean {
  return domain.length >= 3 && domain.includes('.');
}

// re-export para que tests no tengan que re-importar de http si quieren controlar errors
export { ApiError };
